import { create } from 'zustand';
import i18next from 'i18next';
import api, { registerAuthFailureHandler } from '../services/api';
import { useOrganizationStore } from './organizationStore';
import { useOrgBlockStore } from './orgBlockStore';
import { VIEW_PREFS_STORAGE_KEY } from '../lib/boqViewPrefs';
import { hasSessionCookie, isPublicAnonymousPath } from '../lib/sessionHint';
import { ACCOUNT_LANGUAGES } from '../lib/uiLanguage';

let _checkAuthInFlight: Promise<void> | null = null;

/**
 * T3038 (odločitev Alena 14, 30.10.2026) — ob začetku seje vmesnik prevzame
 * jezik računa. Zaznava v `i18n.ts` (localStorage → piškotek → brskalnik)
 * velja le za neprijavljene; prej je na drugi napravi vmesnik ostal v
 * zaznanem jeziku, izvozi in e-pošta pa so sledili `user.language`.
 *
 * Kliče se SAMO ob prijavi (`login`) in ob prvem preverjanju seje, nikoli ob
 * tihem osveževanju (`checkAuth(true)` na prijavljenem): tam bi star odgovor
 * `auth/me/` med letečim `PATCH` preklopnika v glavi vmesnik vrnil nazaj.
 * Menja se samo vmesnik — zahtevka na zaledje ni, zato zanke ni. Primerja se
 * primarni tag (`sl-SI` → `sl`).
 */
function applyAccountLanguage(user: User | null | undefined): void {
  const code = (user?.language || '').toLowerCase();
  if (!(ACCOUNT_LANGUAGES as readonly string[]).includes(code)) return;
  const ui = (i18next.resolvedLanguage || i18next.language || '').toLowerCase().split('-')[0];
  if (ui === code) return;
  void Promise.resolve(i18next.changeLanguage(code)).catch(() => undefined);
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  avatar: string | null;
  language: string;
  timezone: string;
  // T1404 — št. dovoljenja zbornice (IZS/ZAPS); vedno niz, nikoli null.
  chamber_license_number: string;
  email_verified: boolean;
  is_site_admin: boolean;
  is_billing_admin: boolean;
  date_joined: string;
  last_login: string | null;
  // T2816: stanje dvostopenjske prijave (`UserSerializer.get_two_factor`).
  two_factor?: {
    enabled: boolean;
    allowed: boolean;
    required: boolean;
    setup_required: boolean;
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  clearAuth: () => void;
  checkAuth: (force?: boolean) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
    user: null,
    isAuthenticated: false,
    isLoading: true,

    // Tokens are now stored in httpOnly cookies set by the server (T258)
    login: (_user: User) => {
      // T1192: zaklep prejšnje seje ne sme preživeti prijave — sicer bi novi
      // uporabnik takoj videl zaslon »organizacija je zaklenjena«, ki velja za
      // nekoga drugega. Stanje se ponovno postavi ob prvem zaklenjenem klicu.
      useOrgBlockStore.getState().clear();
      set({ user: _user, isAuthenticated: true, isLoading: false });
      // T3038: nov uporabnik (tudi po odjavi prejšnjega) dobi svoj jezik.
      applyAccountLanguage(_user);
    },

    logout: async () => {
      try {
        // Server reads refresh_token from cookie and blacklists it
        await api.post('auth/logout/', {});
      } catch (e) {
        console.warn('Logout request failed', e);
      }
      get().clearAuth();
    },

    clearAuth: () => {
      set({ user: null, isAuthenticated: false, isLoading: false });
      // Drop any tenant-scoped state so the next account starts clean (no
      // cross-tenant leakage of the previously selected organization).
      useOrganizationStore.getState().reset();
      // T1192: zaklep je vezan na organizacijo prejšnje seje — ob odjavi pade.
      useOrgBlockStore.getState().clear();
      // T1160: nastavitve pogleda BoQ so vezane na brskalniški profil, ne na
      // račun. Brez tega bi na deljenem računalniku naslednji uporabnik ob
      // prvem odprtju istega projekta podedoval vklopljeno prodajno ceno
      // prejšnjega. Cena tega je, da so po odjavi cenovni preklopi vedno
      // `off` — varnejši privzetek od podedovanega.
      localStorage.removeItem(VIEW_PREFS_STORAGE_KEY);
    },

    checkAuth: async (force = false) => {
      if (!force && _checkAuthInFlight) return _checkAuthInFlight;

      // `force` re-fetches /auth/me/ even when already authenticated, so the
      // store reflects server-side changes (e.g. a freshly uploaded avatar)
      // without a full page reload (T916). Without it the short-circuit below
      // returns the stale user and the UI keeps showing the old data.
      if (!force && get().isAuthenticated && get().user) {
        set({ isLoading: false });
        return;
      }

      // T1858: gostujoča stran `/share/:token` je namenjena tuji stranki brez
      // računa. Brez piškotka seje bi `auth/me/` vrnil 401, interceptor pa nato
      // poslal `token/refresh/` brez osvežitvenega piškotka (400) — dve rdeči
      // napaki v konzoli pred prvim klikom. Prijavljen član (piškotek obstaja)
      // gre skozi normalno preverjanje, da mu seja ostane.
      if (
        !force &&
        typeof window !== 'undefined' &&
        isPublicAnonymousPath(window.location.pathname) &&
        !hasSessionCookie()
      ) {
        set({ isLoading: false });
        return;
      }

      // Silent (tih) refresh: kadar je uporabnik ŽE prijavljen, force=true samo
      // osveži /me/ v ozadju — NE dvigujemo globalnega isLoading. App ob isLoading
      // zamenja <Routes> za splash in unmounta trenutno stran; pri force-refreshu
      // na prijavljenem uporabniku (npr. EmailVerifyPage checkAuth(true)) bi to
      // sprožilo remount → neskončno zanko (A1, T1004). Začetni checkAuth() na
      // mountu (force=false, še ni prijavljen) mora normalno dvigniti isLoading,
      // da se splash pokaže do prve avtentikacije.
      const silent = get().isAuthenticated && !!get().user;
      if (!silent) set({ isLoading: true });
      _checkAuthInFlight = (async () => {
        try {
          // Cookie is sent automatically; server validates it
          const response = await api.get('auth/me/');
          set({ user: response.data, isAuthenticated: true });
          // T3038: samo ob začetku seje, ne ob tihem osveževanju (gl. zgoraj).
          if (!silent) applyAccountLanguage(response.data);
        } catch {
          get().clearAuth();
        } finally {
          if (!silent) set({ isLoading: false });
          _checkAuthInFlight = null;
        }
      })();
      return _checkAuthInFlight;
    },
}));

registerAuthFailureHandler(() => {
  useAuthStore.getState().clearAuth();
});
